import React, { useMemo } from "react";

/**
 * Block
 * A positioned/sized visual for a single packed column.
 *
 * Props:
 * - block: {
 *     xIn, yIn, widthIn, depthIn, rotated, tiers, heightIn, weightLb, levels
 *   }
 * - pxPerIn: number               // pixels per inch for canvas scaling
 * - orderColors: Record<string, string>
 * - tooltipFn?: (block) => string // optional; default uses no tooltip
 * - gradientAngle?: number        // default 45deg; 90 for left→right, 180 for top→bottom
 */
export default function Block({
    block,
    pxPerIn,
    orderColors = {},
    tooltipFn,
    overflow = false,
}) {
    const {
        xIn = 0,
        yIn = 0,
        widthIn = 0,
        depthIn = 0,
        rotated = false,
        tiers = 1,
        heightIn,
        weightLb,
        levels = [],
    } = block || {};

    const left = yIn * pxPerIn;   // length -> X
    const top = xIn * pxPerIn;    // width  -> Y
    const width = depthIn * pxPerIn;
    const height = widthIn * pxPerIn;

    // Gather colors from levels/skids for gradient
    const backStyle = useMemo(() => {
        const colors = (levels ?? [])
            .flatMap(lvl => (lvl?.skids ?? []).map(sk => orderColors[sk?.order_no] || "#000"));

        if (!colors || colors.length === 0) {
            return { background: "#999" };
        }
        if (colors.length === 1) {
            return { background: colors[0] };
        }

        // Single transition per color, evenly spaced
        const step = 100 / (colors.length - 1);
        const stops = colors.map((c, i) => `${c} ${+(i * step).toFixed(3)}%`).join(", ");
        return { background: `linear-gradient(15deg, ${stops})` };
    }, [levels, orderColors]);

    const title = tooltipFn ? tooltipFn(block) : undefined;

    return (
        <div
            className={`tp-block ${overflow ? "overflow" : "no-overflow"}`}
            title={title}
            style={{
                "--tp-left": `${left}px`,
                "--tp-top": `${top}px`,
                "--tp-w": `${width}px`,
                "--tp-h": `${height}px`,
                "--tp-bg": "#64748b",
                ...backStyle,
            }}
        >
            {rotated && <div className="tp-tag tp-tag--orient">R</div>}
            {tiers > 1 && <div className="tp-tag tp-tag--tiers">T{tiers}</div>}

            <div className="tp-label">
                {Math.round(widthIn)}"×{Math.round(depthIn)}"×{Math.round(heightIn ?? 0)}"
                <div className="tp-label__levels">
                    {levels.map((lvl, idx) => {
                        const skids = Array.isArray(lvl?.skids) ? lvl.skids : [];
                        return (
                            <div key={idx} className="tp-label__stack">
                                {skids.map((sk, j) => {
                                    const details = sk?.details || {};
                                    const orderNo = sk?.order_no ?? details.order_no;
                                    const orderGrp = sk?.order_group ?? details.order_group;
                                    const backorder = sk?.backorder__order_no ?? details.backorder;

                                    return (
                                        <div key={j} className="tp-label__skid">
                                            {orderNo && (
                                                <span
                                                    className="tp-label__order"
                                                    style={{
                                                        "--tp-order-color": orderColors[orderNo] || "#000000",
                                                    }}
                                                >
                                                    {orderNo} {orderGrp} {backorder}
                                                </span>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        );
                    })}
                </div>
                {Number.isFinite(weightLb) && (
                    <>
                        <br />
                        <span className="tp-label__stack">{Math.round(weightLb)} lbs</span>
                    </>
                )}
            </div>
        </div>
    );
}
